import { useCallback, useState, type InputHTMLAttributes, type ReactNode } from "react";

export function Field({
  label,
  hint,
  ...input
}: { label: string; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      <input {...input} />
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export function ErrorNotice({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="notice" role="alert">
      {error}
    </p>
  );
}

/** Runs an async action, tracking pending state and a readable error message. */
export function useAction() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (fn: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setPending(false);
    }
  }, []);
  return { pending, error, setError, run };
}

/** API messages are lower-case fragments ("candidate not found"); show them as sentences. */
function sentence(message: string): string {
  const s = message.charAt(0).toUpperCase() + message.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** Cognito and API errors, phrased for people. */
export function friendlyError(err: unknown): string {
  const e = err as { name?: string; message?: string };
  // The browser's own words for a dropped connection: Chrome, Firefox, Safari, then Amplify's.
  if (e.name === "NetworkError" || (e.name === "TypeError" && /failed to fetch|networkerror|load failed|network connection/i.test(e.message ?? ""))) {
    return "Can't reach KuduRMS. Check your internet connection and try again.";
  }
  switch (e.name) {
    case "NotAuthorizedException":
      return "That email and password don't match.";
    case "UserNotFoundException":
      return "That email and password don't match.";
    case "UsernameExistsException":
      return "There's already a login for this email. Log in instead.";
    case "CodeMismatchException":
      return "That code isn't right. Check it and try again.";
    case "ExpiredCodeException":
      return "That code has expired. We've sent a new one.";
    case "InvalidPasswordException":
      return "Passwords need 12+ characters with upper and lower case, a number and a symbol.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Wait a minute and try again.";
    default:
      return e.message ? sentence(e.message) : "Something went wrong. Try again.";
  }
}
